import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Download, Send, FileText, Loader2, Trash, RefreshCw, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { Input } from '@/components/ui/input';
import { formatInTimeZone } from 'date-fns-tz';
import { eventService } from '@/services/eventService';
import { useQueryClient } from '@tanstack/react-query';
import { BASE_URL } from '@/config/api';
import moment from 'moment';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

interface InvoiceItem {
  date:string;
  description: string;
  quantity: number;
  unitPrice: number;
  categoryName: string;
  countStudents: number;
  total: number;
  event_extra_expenses: number;
  eventType: number;
}

interface TaxeItem {
  name: string;
  rate: number;
  amount: number;
  tax_number: string;
}

interface ExpenseItem {
  name: string;
  amount: number;
}

interface Invoice {
  id: string;
  number: string;
  name: string;
  filename: string;
  is_recurring: boolean;
  discount_scope: string;
  date_recurring?: string;
  date_recurring_next?: string;
  isRecurringValid?: boolean;
  recurrence_frequency?: string;
  amount_discount_1: number;
  discount_percent_1: number;
  totalExtraExpenses: number;
  invoice_filename?: string;
  client_type?: string;
  note?: string;
  tax_amount: number;
  invoice_currency: string;
  date: string;
  period_starts?: string;
  period_ends?: string;
  lastSendedEmail: string;
  dueDate: string;
  status: 'paid' | 'pending' | 'paid_cash' | 'paid_card';
  student: {
    name: string;
    email: string;
    address: string;
  };
  coach: {
    name: string;
    company?: string;
    address: string;
    email: string;
    phone: string;
    notes: string;
    vatNumber?: string;
  };
  emails?: {
    student?: string;
    mother?: string;
    father?: string;
    [key: string]: string | undefined;
  };
  items: InvoiceItem[];
  taxes: TaxeItem[];
  expenses: ExpenseItem[];
  subtotal: number;
  extra_1: number;
  extra_1_description: string;
  taxRate: number;
  taxAmount: number;
  total: number;
  hasPaymentMethods: boolean;
  paymentMethods: string[];
  paymentInstructions: {
    bank?: string;
    account?: string;
    iban?: string;
    swift?: string;
    bankUser?: string;
    bankAddress?: string;
  };
  paymentMethod: string;
  type?: string;
}

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const isMobile = useIsMobile();
  const [selectedEmails, setSelectedEmails] = useState<Record<string, boolean>>({});
  const [showStatusWarningDialog, setShowStatusWarningDialog] = useState(false);
  const isCoach = user?.account_type === 'S' || (user?.account_type === 'C' && invoice?.client_type !== "teacher");
  const isStudent = user?.account_type === 'Student';
  const isDisplayStudentCount = Boolean(user.teacher.display_student_count);
  const [isDownloading, setIsDownloading] = useState(false);
  const isStripePaymentMethod = invoice && invoice.paymentMethods && invoice.paymentMethods.some((method:any) => method.type === "Stripe");
  const [newEmail, setNewEmail] = useState("");
  const [createNewEmail, setCreateNewEmail] = useState(false);
  const queryClient = useQueryClient();


  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}/api/invoice/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch invoice');
      }

      const data = await response.json();
      setInvoice(data.invoice);
      if (data.invoice.emails) {
        const initialEmails: Record<string, boolean> = {};
        Object.entries(data.invoice.emails).forEach(([key, email]) => {
          if (email) {
            initialEmails[key] = true;
          }
        });
        setSelectedEmails(initialEmails);
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast({
        title: t('invoiceDetail.errorFetchingInvoice'),
        description: String(error),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [id, t, toast, token]);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/api/invoice/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'Accept' : 'application/json',
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch invoice');
        }
      
        const data = await response.json();
        setInvoice(data.invoice);

        if (data.invoice.emails) {
          const initialEmails: Record<string, boolean> = {};
          Object.entries(data.invoice.emails).forEach(([key, email]) => {
            //console.log(email);
            if (email) {
              initialEmails[key] = true;
            }
          });
          setSelectedEmails(initialEmails);
        }
      } catch (error) {
        console.error('Error fetching invoice:', error);
        toast({
          title: t('invoiceDetail.errorFetchingInvoice'),
          description: String(error),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchInvoice();
    }
  }, [id, t, toast]);

  const isSendButtonDisabled = () => {
    const emailsToSend = Object.entries(selectedEmails).filter(([_, isSelected]) => isSelected).map(([type]) => type);
    return sendingInvoice || (emailsToSend.length === 0 && newEmail === "");
  };

  const formatDate = (dateString: string) => {
    const date = moment(dateString);
    //console.log(locale);
    return date.format(locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY');
  };

  const formatCurrency = (amount: number) => {
    const usedLocale = locale === 'fr' ? 'fr-CA' : 'en-US';
    return new Intl.NumberFormat(usedLocale, {
      style: 'currency',
      currency: invoice.invoice_currency || user.currency_code
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'paid_cash':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'paid_card':
        return 'bg-green-100 text-green-800 hover:bg-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 hover:bg-red-200';
      default:
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
    }
  };

  const openSendDialog = () => {
    setShowEmailDialog(true);
  };

  const handleCheckboxChange = (emailType: string, checked: boolean) => {
    setSelectedEmails(prev => ({
      ...prev,
      [emailType]: checked
    }));
  };
  
  const sendInvoice = async () => {
    if (!id || !invoice) return;
    
    const emailsToSend = Object.entries(selectedEmails)
      .filter(([_, isSelected]) => isSelected)
      .map(([type]) => type);
    
    if (emailsToSend.length === 0 && newEmail === "") {
      toast({
        title: t('invoiceDetail.noEmailsSelected'),
        description: t('invoiceDetail.pleaseSelectEmails'),
        variant: 'destructive',
      });
      return;
    }
  
    try {
      setSendingInvoice(true);
      const response = await fetch(`${BASE_URL}/api/invoice/send/${id}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email_types: emailsToSend,
            new_email: newEmail,
            create_new_email: createNewEmail
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invoice');
      }
  
      setShowEmailDialog(false);
      toast({
        title: t('invoiceDetail.sentSuccess'),
        description: t('invoiceDetail.sentSuccessDescription'),
      });
      navigate('/invoices/billing');
    } catch (error) {
      toast({
        title: t('invoiceDetail.sentError'),
        description: String(error),
        variant: 'destructive',
      });
    } finally {
      setSendingInvoice(false);
    }
  };
  
  const downloadPDF = async () => {
    if (!id || !invoice) return;
  
    try {
       setIsDownloading(true);
      const response = await fetch(`${BASE_URL}/api/invoice/download/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
  
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.name}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: t('invoiceDetail.downloadError'),
        description: t('invoiceDetail.fetchFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const confirmDeleteInvoice = () => {
    if (!invoice) return;
    
    if (invoice.status === 'pending') {
      deletePDF();
    } else {
      setShowStatusWarningDialog(true);
    }
  };

  const deletePDF = async () => {
    if (!id || !invoice) return;
    setDeletingInvoice(true);
    try {
      const response = await fetch(`${BASE_URL}/api/invoice/delete/${id}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send invoice');
      }

      navigate('/invoices');
      setDeletingInvoice(false);
  
      toast({
        title: t('invoiceDetail.deleteSuccess'),
        description: t('invoiceDetail.deleteSuccessDescription'),
      });

      eventService.clearEventsCache();
      queryClient.clear();

    } catch (err) {
      setDeletingInvoice(false);
      toast({
        title: t('invoiceDetail.downloadError'),
        description: t('invoiceDetail.fetchFailed'),
        variant: 'destructive',
      });
    }
  };
  

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin inline-block" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 container py-8 mt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">{t('invoiceDetail.notFound')}</h1>
            <p className="text-muted-foreground mb-4">{t('invoiceDetail.notFoundDescription')}</p>
            <Button onClick={() => navigate('/invoices')}>{t('common.goBack')}</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container py-8 mt-16">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices')}
            className="mb-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8">
            <Card className="p-8 max-w-4xl relative">
              <CardContent className="p-0">

                {invoice.type == "3" && invoice.status == "pending" && (
                <Card className="pb-0 cursor-pointer mt-5 items-center justify-center mb-3">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md items-center justify-center p-2 text-yellow-800 font-medium flex items-center">
                  {t('invoiceDetail.shopPending')}
                  </div>
                </Card>
                )}

                <h2 className="text-2xl font-bold">{t('invoiceDetail.invoice')}</h2>
                <p className="text-muted-foreground">{invoice.name}</p>
                {invoice.period_starts && invoice.period_ends ? (
                  <p className='text-sm text-muted-foreground'>
                    {t('invoices.billingPeriod')}: {formatDate(invoice.period_starts)} - {formatDate(invoice.period_ends)}
                  </p>
                ) : null}
                <span className='text-sm text-muted-foreground'>{invoice !== null && invoice.lastSendedEmail && invoice.lastSendedEmail !== null ? '(' +  t('invoices.sended') : ""} {invoice !== null && invoice.lastSendedEmail && invoice.lastSendedEmail !== null ? formatDate(invoice.lastSendedEmail) + ')' : ""}</span>
                <div className='absolute right-7 top-5'> {/* Ensure full width */}
                  <div></div> {/* Empty div to push the badge to the right */}
                  <Badge
                    variant="outline"
                    className={`${getStatusColor(invoice.status)}`}
                  >
                    {t(`invoices.status.${invoice.status}`)}
                  </Badge>
                </div>
             
                <div className="flex justify-between items-start mb-8 border-b pb-3">
                  <div></div>
                </div>

                <div
                  className={`grid grid-cols-1 mb-5 ${
                    isMobile ? 'w-full' : 'w-96 ml-auto'
                  }`}
                >
                  <div className="flex justify-between">
                    <p className="text-muted-foreground">{t('invoiceDetail.invoiceDate')}</p>
                    <p>{formatDate(invoice.date)}</p>
                  </div>
                  <div className="flex justify-between">
                    <p className="text-muted-foreground">{t('invoiceDetail.dueDate')}</p>
                    <p>{formatDate(invoice.dueDate)}</p>
                  </div>
                </div>


                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div className='bg-gray-100 border rounded-lg p-3 min-h-28'>
                      {/*<h3 className="font-semibold text-muted-foreground mb-2">{t('invoiceDetail.from')}</h3>*/}
                      <div className="space-y-1">
                        <p className="font-semibold text-sm">{invoice.coach.name}</p>
                        {invoice.coach.company && <p>{invoice.coach.company}</p>}
                        <p className="whitespace-pre-line text-sm">{invoice.coach.address}</p>
                        <p className='text-sm'>{invoice.coach.email}</p>
                        <p className='text-sm'>{invoice.coach.phone}</p>
                        <p className='text-sm'>{invoice.coach.notes}</p>
                        {invoice.coach.vatNumber && (
                          <p>
                            {t('invoiceDetail.vatNumber')}: {invoice.coach.vatNumber}
                          </p>
                        )}
                    </div>
                  </div>

                  <div className='bg-gray-100 border rounded-lg p-3 min-h-28'>
                    {/*<h3 className="font-semibold text-muted-foreground mb-2">{t('invoiceDetail.to')}</h3>*/}
                    <div className="space-y-1">
                      <p className="font-semibold text-sm">{invoice.student.name}</p>
                      <p className="whitespace-pre-line text-sm">{invoice.student.address}</p>
                      <p className='text-sm'>{invoice.student.email}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <div className="rounded-md border overflow-hidden">
                    <div className="w-full overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50">
                          <tr className="text-left">
                            <th className="p-3 font-medium">{t('invoiceDetail.date')}</th>
                            {!isMobile && (
                              <th className="p-3 font-medium">{t('invoiceDetail.description')}</th>
                            )}
                            <th className="p-3 font-medium text-right">{t('invoiceDetail.quantity')}</th>
                            <th className="p-3 font-medium text-right">{t('invoiceDetail.total')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {invoice.items.map((item, index) => (
                            <tr key={index}>
                              <td className="p-3">{formatDate(item.date)}</td>
                              {!isMobile && (
                                <td className="p-3 text-sm max-w-[270px]">
                                  {(item.eventType != 10 && invoice.type != "2") && (
                                    <span className="badge mr-1 bg-gray-200 text-xs text-gray-400 font-medium px-2 py-0.5 rounded-full">
                                      {t('agenda.Event')}
                                    </span>
                                  )} 
                                {item.eventType === 10 ? '' + item.categoryName + ' ' : ' '}
                                {item.eventType === 10 && isDisplayStudentCount && (
                                 <span className='text-xs'>
                                 {item.countStudents > 1 ? '(' + t('common.with') : '(' + t('agenda.privateLesson') + ')'}
                                 {item.countStudents > 1 ? ' ' + item.countStudents : ''} {item.countStudents > 1 ? t('students.students') + ')' : ''}
                                 </span>
                                )}
                                 <p>{item.eventType != 10 ? item.description !== "Lesson" && item.description !== "Event" ? item.description + ' ' : '' : ''}</p>  
                                </td>
                              )}
                              <td className="p-3 text-right">
                                {item.eventType === 10 ? (item.quantity ? item.quantity + ' Mn.' : '-') : '-'}
                              </td>
                              <td className="p-3 text-right">
                                {formatCurrency(item.total)}
                                {item.event_extra_expenses > 0 && (
                                <>
                                <br/>
                                  <span className="text-xs">
                                    + {formatCurrency(item.event_extra_expenses)} {t('invoices.extraCharges')}
                                  </span>
                                </>
                              )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-2 mb-8">

                  
                  <div className="flex justify-between w-full max-w-xs">
                    <p className="text-muted-foreground">{t('invoiceDetail.subtotal')}</p>
                    <p>{formatCurrency(invoice.subtotal)}</p>
                  </div>

                   {invoice.amount_discount_1 > 0 && (
                    <div className="flex justify-between w-full max-w-xs">
                      <p className="text-muted-foreground">{t('invoiceDetail.discount')} <span className='text-xs'>(-{invoice.discount_percent_1}%)</span> <span className='text-xs'>{invoice.discount_scope === 'all' ? "" : invoice.discount_scope === 'lessons' ? '(' + t('invoiceDetail.discountScopeLessons') + ')' : '(' + t('invoiceDetail.discountScopeEvents') + ')'}</span> </p>
                      <p>-{formatCurrency(invoice.amount_discount_1)}</p>
                    </div>
                  )}

                  {invoice.totalExtraExpenses > 0 && (
                    <div className="flex justify-between w-full max-w-xs">
                      <p className="text-muted-foreground">{t('invoiceDetail.extra')}</p>
                      <p>{formatCurrency(invoice.totalExtraExpenses)}</p>
                    </div>
                  )}
                 
                  <div className="justify-between w-full max-w-xs text-sm">
                    {invoice.taxes.map((item, index) => (
                      <div key={index} className="flex justify-between w-full max-w-xs">
                        <p className="text-muted-foreground">{item.name} {item.tax_number && (<span className="text-xs">({item.tax_number})</span>)} {item.rate}%</p>
                        <p>{formatCurrency(item.amount)}</p>
                      </div>
                    ))}
                  </div>
                  
                  {invoice.extra_1 > 0 && (
                    <div className="flex justify-between w-full max-w-xs">
                      <p className="text-muted-foreground">{t('invoiceDetail.extra')}: {invoice.extra_1_description}</p>
                      <p>{formatCurrency(invoice.extra_1)}</p>
                    </div>
                  )}
  

                  <div className="justify-between w-full max-w-xs">
                    {invoice.expenses.map((item, index) => (
                      <div key={index} className="flex justify-between w-full max-w-xs">
                        <p className="text-muted-foreground">{item.name}</p>
                        <p>{formatCurrency(item.amount)}</p>
                      </div>
                    ))}
                  </div>

                 

                  <Separator className="my-2 w-full max-w-xs" />
                  <div className="flex justify-between w-full max-w-xs font-semibold text-lg">
                    <p>{t('invoiceDetail.total')}</p>
                    <p className='text-lg'>{formatCurrency(invoice.total)}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm border-l-2 pl-3 py-1 border-muted">
                    {invoice.note}
                  </div>
                </div>

                <div className="pt-6 flex flex-nowrap gap-x-4">   
                  {/* Bloc des instructions bancaires */}
                  {(!!invoice.paymentInstructions.bank ||
                    !!invoice.paymentInstructions.account ||
                    !!invoice.paymentInstructions.iban ||
                    !!invoice.paymentInstructions.swift) && (
                    <div className="border p-3 w-full md:w-1/2">
                      <h3 className="font-semibold mb-1">
                        {t('invoiceDetail.paymentMethod')}
                      </h3>

                      {invoice.paymentInstructions && (
                        <>
                          {(user?.teacher?.country_code === 'FR' || user?.teacher?.country_code === 'CH') ? (
                            <>
                              {invoice.paymentInstructions.bankUser && <p className='text-sm'>{t('invoiceDetail.bankUser')}: {invoice.paymentInstructions.bankUser}</p>}
                              {invoice.paymentInstructions.bankAddress && <p className='text-sm'>{t('invoiceDetail.bankAddress')}: {invoice.paymentInstructions.bankAddress}</p>}
                              {invoice.paymentInstructions.bank && <p className='text-sm'>{t('invoiceDetail.bankName')}: {invoice.paymentInstructions.bank}</p>}
                              {invoice.paymentInstructions.account && <p className='text-sm'>{t('invoiceDetail.bankAccount')}: {invoice.paymentInstructions.account}</p>}
                              {invoice.paymentInstructions.iban && <p className='text-sm'>{t('invoiceDetail.banIBAN')}: {invoice.paymentInstructions.iban}</p>}
                              {invoice.paymentInstructions.swift && <p className='text-sm'>{t('invoiceDetail.bankSwift')}: {invoice.paymentInstructions.swift}</p>}
                            </>
                          ) : (
                            <>
                              {invoice.paymentInstructions.bank && <p>{invoice.paymentInstructions.bank}</p>}
                              {invoice.paymentInstructions.account && <p>{invoice.paymentInstructions.account}</p>}
                              {invoice.paymentInstructions.iban && <p>{invoice.paymentInstructions.iban}</p>}
                              {invoice.paymentInstructions.swift && <p>{invoice.paymentInstructions.swift}</p>}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Bloc des autres méthodes de paiement */}
                  {invoice.paymentMethods?.length > 0 && (
                    <div className="border p-3 w-full md:w-1/2">
                      <h3 className="font-semibold mb-1">{t('invoices.registeredPaymentMethods')}</h3>
                        {invoice.paymentMethods.map((paymentMethod: any, index: number) => (
                        <div key={index}>
                          {paymentMethod.type === 'PayPal' && (
                            <>
                              <span className="text-xs">{t('invoiceDetail.paypalPayment')}</span><br></br>
                              <span className='text-sm'>{paymentMethod.details?.paypal || 'N/A'}</span>
                            </>
                          )}
                          {paymentMethod.type === 'Stripe' && (
                            <span className='text-sm'>{t('invoiceDetail.onlinePayment')}</span>
                          )}
                          {paymentMethod.type === 'IBAN' && (
                            <>
                              <span>IBAN N°: {paymentMethod.details?.iban_number || 'N/A'}</span>
                              <br />
                              <span>SWIFT N°: {paymentMethod.details?.swift_number || 'N/A'}</span>
                            </>
                          )}
                          {paymentMethod.type === 'Swift' && (
                            <span>{paymentMethod.details?.swift_number || 'N/A'}</span>
                          )}
                          {paymentMethod.type === 'Cash' && (
                            <>
                            <span className="text-sm">{t('invoiceDetail.cashPayment')}</span><br></br>
                            <span className='text-xs'>{paymentMethod.details?.cash || ''}</span>
                            </>
                          )}
                          {paymentMethod.type === 'E-Transfer' && (
                            <>
                              <span className="text-xs">{t('invoiceDetail.eTransferPayment')}</span><br></br>
                              <span className='text-sm'>{paymentMethod.details?.eTransfer || 'N/A'}</span>       
                            </>
                          )}
                          {paymentMethod.type === 'Bank' && (
                            <ul>
                              {paymentMethod.details?.custom_fields?.length > 0 ? (
                                paymentMethod.details.custom_fields.map((field, idx) => (
                                  <li key={idx}>
                                    <strong>{field.name}:</strong> {field.value}
                                  </li>
                                ))
                              ) : (
                                <li>No custom fields added.</li>
                              )}
                            </ul>
                          )}
                          {paymentMethod.type === 'Venmo' && (
                            <>
                              <span className='text-sm'>Venmo ID: {paymentMethod.details?.venmo || 'N/A'}</span>
                              {paymentMethod.details?.qrcode && (
                                <div>
                                  <img
                                    src={paymentMethod.details.qrcode}
                                    alt="Venmo QR Code"
                                    className="w-[50px] h-[50px]"
                                  />
                                </div>
                              )}
                            </>
                          )}
                          {paymentMethod.type === 'QRCode' && (
                            <>
                              <span className='text-xs'>Scan QRCode</span>
                              {paymentMethod.details?.qrcode && (
                                <div>
                                  <img
                                    src={paymentMethod.details.qrcode}
                                    alt="QR Code"
                                    className="w-[50px] h-[50px]"
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="md:col-span-3">
            <div className="flex flex-col gap-2">
              {Boolean(invoice.is_recurring) && (
                <Card className="mb-2 bg-blue-50 border-blue-200 shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-full shrink-0">
                        <RefreshCw className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-blue-900">
                          {t('invoices.recurrentInvoice')} {invoice.recurrence_frequency ? <small>({t('agenda.' + invoice.recurrence_frequency.toLocaleLowerCase())})</small> : ''}
                        </h4>

                        {invoice.date_recurring_next && (
                          <p className="text-xs text-blue-700 mt-1">
                            {t('invoices.nextInvoiceDate')}: {formatDate(invoice.date_recurring_next)}
                          </p>
                        )}

                        {invoice.date_recurring && (
                          <p className="text-xs text-blue-700 mt-1">
                            {t('invoices.lastInvoiceDate')}: {formatDate(invoice.date_recurring)}
                          </p>
                         )}

                         {invoice.date_recurring && !invoice.isRecurringValid && (
                          <p className="text-xs text-red-700 mt-1">
                            {t('invoices.recurrenceStopped')}
                          </p>
                         )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Button variant="outline" className="w-full" onClick={downloadPDF} disabled={isDownloading}>
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('invoices.downloading')}
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    {t('invoiceDetail.download')}
                  </>
                )}
              </Button>
                {isCoach && invoice.type != "3" && (
                  <div>
                    <Button variant="outline" className="w-full mt-1" onClick={openSendDialog}>
                      <Send className="mr-2 h-4 w-4" />
                      {t('invoiceDetail.send')}
                    </Button>
                    {invoice.status === "pending" && (
                      <Button 
                        variant="outline" 
                        className="w-full mt-3" 
                        onClick={() => navigate(`/invoice/edit/${id}`)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        {t('invoiceDetail.edit')}
                      </Button>
                    )}
                    <Button variant="destructive" className="w-full mt-3" onClick={confirmDeleteInvoice}>
                      {deletingInvoice ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        </>
                      ) : (
                        <>
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('invoiceDetail.delete')}
                        </>
                      )}
                    </Button>
                  </div>
                )}
                 {isStudent && invoice.status === "pending" && invoice.type != "3" && isStripePaymentMethod && (
                  <Button
                    className="w-full mt-1"
                    onClick={() => navigate('/payment', { state: { invoice } })}
                  >
                    {t('invoiceDetail.pay')}
                  </Button>
                 )}

                 {invoice.status === "pending" && invoice.type == "3" && isStripePaymentMethod && (
                    <div className="flex items-center space-x-2">
                      <Button
                        className="w-full"
                        onClick={fetchInvoice}
                      > <RefreshCw />
                        {t('invoiceDetail.refresh')}
                      </Button>
                    </div>
                  )}
            </div>
          </div>
        </div>
      </main>

      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className={isMobile ? "p-0 w-full h-[100dvh] max-h-[100dvh]" : "max-w-[600px]"}>
          <DialogHeader className={isMobile ? "px-6 py-4 border-b mt-1" : "pb-2"}>
            <DialogTitle>
              <div className="flex items-center gap-2">
                {t('invoiceDetail.sendToRecipients')}
              </div>
              </DialogTitle>
          </DialogHeader>
          
          <div className={isMobile ? "py-4 space-y-4 p-5" : "py-4 space-y-4"}>

          <p className='text-sm text-muted-foreground'>{showEmailDialog && invoice !== null && invoice.lastSendedEmail && invoice.lastSendedEmail !== null ? t('invoices.sended') : ""} {showEmailDialog && invoice !== null && invoice.lastSendedEmail && invoice.lastSendedEmail !== null ? formatDate(invoice.lastSendedEmail) : ""}</p>

          <p className="text-sm">{t('invoiceDetail.selectRecipients')}</p>
            
            {invoice.emails && Object.entries(invoice.emails).map(([emailType, email]) => 
              email ? (
                <div key={emailType} className="flex items-center space-x-2">
                  <Checkbox 
                    id={`email-${emailType}`} 
                    checked={!!selectedEmails[emailType]}
                    onCheckedChange={(checked) => handleCheckboxChange(emailType, checked === true)}
                  />
                  <Label htmlFor={`email-${emailType}`} className="flex-1">
                    <span className="capitalize mr-2">
                      {emailType === "student" && invoice.client_type === "teacher" ? t('teachers.teacher') : emailType}:
                    </span>
                    <span className="text-muted-foreground">{email}</span>
                  </Label>
                </div>
              ) : null
            )}
            
            {(!invoice.emails || Object.values(invoice.emails).filter(Boolean).length === 0) && (
              <p className="text-sm text-muted-foreground">{t('invoiceDetail.noEmailsAvailable')}</p>
            )}

            {(!invoice?.emails || Object.values(invoice?.emails || {}).filter(Boolean).length === 0) && (
                  <div className="items-center space-x-2 space-y-4">
                    <Input
                      type="email"
                      placeholder={t('students.email')}
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                    />
                    <Checkbox
                      id="create-new-email"
                      checked={createNewEmail}
                      onCheckedChange={(checked) => setCreateNewEmail(checked === true)}
                    />
                    <Label htmlFor="create-new-email" className='text-xs'>
                      {t('invoiceDetail.createSenderEmail')}
                    </Label>
                </div>
              )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={sendInvoice} disabled={isSendButtonDisabled()}>
              {sendingInvoice ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.sending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('invoiceDetail.sendInvoice')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <AlertDialog open={showStatusWarningDialog} onOpenChange={setShowStatusWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoiceDetail.cannotDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoiceDetail.cannotDeleteMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ok')}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      <Footer />
    </div>
  );
};

export default InvoiceDetail;